import { useEffect, useMemo } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useHistory } from "react-router";
import { FlightShopStep } from "redmond";
import { populateFlightShopQueryParams } from "../../shop/actions/actions";
import { flightShopProgressSelector } from "../../shop/reducer";
import { useFlightList } from "./useFlightList";

export const useAdjustedChfarFareById = (id: string) => {
  const dispatch = useDispatch();
  const history = useHistory();
  const flightShopProgress = useSelector(flightShopProgressSelector);

  const { data, isLoading, isError, isFetching } = useFlightList();

  const adjustedChfarFare = useMemo(() => data?.flights.fares[id], [data, id]);

  // There are times when the shop data needs to be refetched.
  // If it doesn't match up with the previously selected fareId,
  // We need to set the progress to the first step and let the customer pick a new flight
  const shouldNavigateToDepartureStep =
    data != null &&
    adjustedChfarFare == null &&
    flightShopProgress !== FlightShopStep.ChooseDeparture;

  useEffect(() => {
    let timeoutId: ReturnType<typeof setTimeout> | number | null = null;

    if (shouldNavigateToDepartureStep) {
      timeoutId = setTimeout(
        () =>
          dispatch(
            populateFlightShopQueryParams({
              history,
              forceQueryUpdate: true,
              newQueryParams: {
                flightShopProgress: FlightShopStep.ChooseDeparture,
              },
            })
          ),
        500
      );
    }

    return () => {
      if (timeoutId) {
        clearTimeout(timeoutId);
      }
    };
  }, [
    shouldNavigateToDepartureStep,
    dispatch,
    populateFlightShopQueryParams,
    history,
  ]);

  return useMemo(
    () => ({ data: adjustedChfarFare, isLoading, isError, isFetching }),
    [adjustedChfarFare, isLoading, isError, isFetching]
  );
};
